// SUBJECT FAMILIES inside a sender group — "5 Threads" (2026-09-15 parity audit, F1-3 / bff#39;
// frames 40008290:153703 → 40010619:262299 → 40010621:263696).
//
// An open group lists its conversations one by one. The frames add a level above that: the group's
// mail clustered by normalised subject — "Weekly LinkedIn update ×120", "You appeared in 8
// searches ×12" — reached from a "5 Threads" row in the Briefing strip, ticked as ONE row standing
// for all its emails, and offered "Archive thread / Delete thread / Create ongoing / Add to
// ongoing". The clustering is the BFF's (`POST v1/email/groups/threads`, largest first); this
// module is what the web does with the answer: the listing rows, the pick a ticked family becomes,
// the counts the strip quotes, and the verbs.
//
// A family pick carries its `timelineIds`, which is what every thread endpoint takes — so archive,
// delete, create and add all run on the machinery the thread picks already use, and a family is
// never a fourth kind of thing to file.
//
// Pure — no store, no api, nothing mounted.

import { lastUpdate } from '@/lib/emailSession'

export const FAMILY_TEXT = {
  /** "5 Threads" — the strip row and the crumb (frames 40008290:153703 / 40010619:262299). */
  threads: (n) => `${Number(n) || 0} Thread${Number(n) === 1 ? '' : 's'}`,
  archive: 'Archive thread',
  delete: 'Delete thread',
  createOngoing: 'Create ongoing',
  addToOngoing: 'Add to ongoing',
}

/** The option ids, so the chooser, the CTA and the commit name them once. */
export const FAMILY_CHOICE = {
  archive: 'family-archive',
  delete: 'family-delete',
  createOngoing: 'family-create-ongoing',
  addToOngoing: 'family-add-ongoing',
}

/** The request body `POST v1/email/groups/threads` takes, from the open group. */
export function familiesRequest(group, take = 50) {
  return {
    sender: String(group?.sender || ''),
    emailAddress: String(group?.emailAddress || '') || undefined,
    take,
  }
}

/** The families an answer carries, junk dropped, in the order the server ranked them (largest first). */
export function readFamilies(payload) {
  const body = payload?.data?.data ?? payload?.data ?? payload ?? {}
  return (Array.isArray(body?.families) ? body.families : [])
    .filter(f => f && (f.key || f.subject))
    .map(f => ({
      key: String(f.key || f.subject),
      subject: String(f.subject || '').trim() || '(no subject)',
      count: Number(f.count) || 0,
      unseenCount: Number(f.unseenCount) || 0,
      lastReceivedUtc: f.lastReceivedUtc || null,
      messageIds: (f.messageIds || []).filter(Boolean).map(String),
      timelineIds: (f.timelineIds || []).filter(Boolean).map(String),
    }))
}

/** One row of the families listing: sender · count · subject · last date (frame 40010619:262299). */
export function familyRow(family, group = null, now = new Date()) {
  return {
    key: family.key,
    sender: String(group?.name || group?.sender || '').trim(),
    subject: family.subject,
    count: family.count,
    time: lastUpdate(family.lastReceivedUtc, now),
    timelineIds: family.timelineIds || [],
  }
}

export function familyRows(families, group = null, now = new Date()) {
  return (families || []).filter(Boolean).map(f => familyRow(f, group, now))
}

/**
 * "LinkedIn 12" — the per-row family count the open group's rows carry (frame 40008290:153703).
 * A thread that belongs to a family of one carries nothing: the frame draws no "1".
 */
export function familyCountOf(threadId, families) {
  const id = String(threadId || '')
  if (!id) return 0
  const family = (families || []).find(f => (f.timelineIds || []).includes(id))
  return family && family.count > 1 ? family.count : 0
}

/** The board id for a ticked family, so ticking it twice is one tick. */
export function familyPickId(key) { return `email-family:${String(key || '')}` }

/** A ticked family, as it goes onto the Work-board — one pick, N emails. */
export function familyPick(family, group = null) {
  return {
    id: familyPickId(family?.key),
    kind: 'email-family',
    key: String(family?.key || ''),
    title: String(family?.subject || '').trim(),
    sender: String(group?.name || group?.sender || '').trim(),
    count: Number(family?.count) || 0,
    timelineIds: (family?.timelineIds || []).filter(Boolean).map(String),
  }
}

/** Only the family picks — the board holds groups, threads, files and research too. */
export function familyPicks(items) {
  return (items || []).filter(i => i && i.kind === 'email-family')
}

/** Every conversation the ticked families stand for, deduplicated. */
export function familyTimelineIds(items) {
  const seen = new Set()
  for (const pick of familyPicks(items)) for (const id of pick.timelineIds || []) seen.add(id)
  return [...seen]
}

/**
 * The chooser for a ticked family (frame 40010621:263696), in the frame's order and words.
 * Delete is real here: the family's conversations go through the same bulk endpoint a thread
 * selection's delete does.
 */
export function familySuggestions({ items = [] } = {}) {
  if (!familyPicks(items).length) return []
  return [
    { id: FAMILY_CHOICE.archive, text: FAMILY_TEXT.archive, recommended: true, ready: true },
    { id: FAMILY_CHOICE.delete, text: FAMILY_TEXT.delete, recommended: false, ready: true },
    { id: FAMILY_CHOICE.createOngoing, text: FAMILY_TEXT.createOngoing, recommended: false, ready: true },
    { id: FAMILY_CHOICE.addToOngoing, text: FAMILY_TEXT.addToOngoing, recommended: false, ready: true },
  ]
}

/** The bulk verb a family option commits, or null. */
export function familyBulkVerb(armed) {
  if (armed === FAMILY_CHOICE.archive) return 'archive'
  if (armed === FAMILY_CHOICE.delete) return 'delete'
  return null
}

/** The CTA's word for an armed family option — the verb itself, as every other chooser's is. */
export function familyCta(armed) {
  if (armed === FAMILY_CHOICE.archive) return FAMILY_TEXT.archive
  if (armed === FAMILY_CHOICE.delete) return FAMILY_TEXT.delete
  if (armed === FAMILY_CHOICE.createOngoing) return FAMILY_TEXT.createOngoing
  return null
}

export default familyRows
